import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Marquee } from "@/components/Marquee";
import { API, COURSES } from "@/constants/site";

const inputCls =
  "w-full px-4 py-3 rounded bg-[#162544] border border-[#203154] text-white text-sm placeholder-[#4b658d] focus:border-[#38bdf8] focus:outline-none transition-colors";

export default function Courses() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", course: COURSES[0].name });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/interest`, form);
      setSubmitted(true);
      toast.success("Interest submitted — our team will reach out with syllabus details.");
      setForm({ name: "", phone: "", email: "", course: COURSES[0].name });
    } catch (err) {
      toast.error("Could not submit right now. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="courses-page">
      {/* PAGE HERO */}
      <section className="py-20 md:py-28 bg-[#0c1427] border-b border-[#203154]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#162544] border border-[#203154] text-[#38bdf8] font-mono text-xs uppercase tracking-widest mb-8 d3-reveal">
            Student Hub · Job-Oriented Programs
          </div>
          <h1 className="font-heavy text-[clamp(38px,5vw,72px)] uppercase text-white leading-[0.92] tracking-tight mb-6">
            <span className="ln"><span>Programs &amp;</span></span>
            <span className="ln"><span className="text-[#38bdf8]">Curriculums.</span></span>
          </h1>
          <p className="text-base md:text-lg text-[#9cb4d8] leading-relaxed max-w-2xl d3-reveal">
            Affordable, job-oriented programs taught by industry-expert trainers — every course
            includes capstone projects, resume reviews, and placement assistance with hiring
            partners.
          </p>
        </div>
      </section>

      <Marquee />

      {/* COURSES GRID */}
      <section className="py-24 md:py-32 bg-[#101b33] border-b border-[#203154]" data-testid="courses-grid-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {COURSES.map((c) => (
              <div
                key={c.track}
                className="p-6 rounded-xl bg-[#0c1427] border border-[#203154] hover:border-[#38bdf8]/50 transition-colors"
                data-testid={`course-card-${c.track.replace(" ", "-").toLowerCase()}`}
              >
                <div className="text-xs font-mono text-[#38bdf8] uppercase mb-2">{c.track}</div>
                <h3 className="text-lg font-bold text-white mb-2">{c.name}</h3>
                <p className="text-xs text-[#9cb4d8] leading-relaxed">{c.blurb}</p>
              </div>
            ))}
            <div className="p-6 rounded-xl bg-[#162544] border border-[#38bdf8]/40 flex flex-col justify-between" data-testid="course-card-placement">
              <div>
                <div className="text-xs font-mono text-[#38bdf8] uppercase mb-2">Career Advantage</div>
                <h3 className="text-lg font-bold text-white mb-2">Placement Readiness</h3>
                <p className="text-xs text-[#9cb4d8]">
                  Dedicated mock interview sessions, recruiter pipelines, and career advice.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* EXPRESS INTEREST FORM */}
      <section className="py-24 md:py-32 bg-[#0c1427]" data-testid="interest-form-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-2xl mx-auto p-8 rounded-xl bg-[#101b33] border border-[#203154]">
            <div className="text-center mb-6">
              <h2 className="text-2xl md:text-3xl font-bold text-white">Express Interest in a Course</h2>
              <p className="text-xs text-[#9cb4d8] mt-2">
                Get syllabus details and schedule a free demo session.
              </p>
            </div>

            {submitted ? (
              <div
                className="p-6 rounded-lg bg-[#162544] border border-[#38bdf8]/40 text-center"
                data-testid="interest-success-message"
              >
                <p className="text-sm text-white font-bold mb-2">Thank you — interest received.</p>
                <p className="text-xs text-[#9cb4d8] mb-4">
                  Our counsellor will contact you shortly with the syllabus and demo schedule.
                </p>
                <button
                  type="button"
                  onClick={() => setSubmitted(false)}
                  data-testid="interest-submit-another-button"
                  className="text-xs font-mono uppercase text-[#38bdf8] hover:text-white transition-colors"
                >
                  Submit another →
                </button>
              </div>
            ) : (
              <form className="space-y-4" onSubmit={onSubmit} data-testid="interest-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    placeholder="Full Name"
                    required
                    className={inputCls}
                    data-testid="interest-name-input"
                  />
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    placeholder="Phone Number"
                    required
                    className={inputCls}
                    data-testid="interest-phone-input"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    placeholder="Email Address"
                    required
                    className={inputCls}
                    data-testid="interest-email-input"
                  />
                  <select
                    name="course"
                    value={form.course}
                    onChange={onChange}
                    className={inputCls}
                    data-testid="interest-course-select"
                  >
                    {COURSES.map((c) => (
                      <option key={c.name} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="interest-submit-button"
                  className="w-full py-3.5 rounded bg-[#38bdf8] text-[#0c1427] font-bold text-xs font-mono uppercase tracking-wider hover:bg-white transition-colors disabled:opacity-60"
                >
                  {submitting ? "Submitting..." : "Submit Expression of Interest"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
